import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, LogIn, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Dados simulados dos projetos para região
const projectsData = [
  {
    id: "Ban12345",
    name: "Moeda Social Carbono - Colônia Z10",
    registryId: "54321",
    bairro: "Cacuia",
    cidade: "Rio de Janeiro",
    estado: "RJ",
    pais: "Brasil",
    region: "RJ/Brasil",
    type: "Mitigação",
    status: "Em implementação",
    lastActivity: "10/04/2025",
    tokens: "3520",
    progress: 70,
    image: "/lovable-uploads/WhatsApp Image 2025-04-03 at 18.32.45 (2).jpeg"
  }
];

// Opções de região disponíveis para filtro
const regionTypes = [
  "Norte",
  "Nordeste",
  "Sul",
  "Sudeste",
  "Centro-Oeste"
];

// Lista de estados do Brasil por região
const estadosPorRegiao = {
  Norte: ["Acre", "Amapá", "Amazonas", "Pará", "Rondônia", "Roraima", "Tocantins"],
  Nordeste: ["Alagoas", "Bahia", "Ceará", "Maranhão", "Paraíba", "Pernambuco", "Piauí", "Rio Grande do Norte", "Sergipe"],
  Sul: ["Paraná", "Rio Grande do Sul", "Santa Catarina"],
  Sudeste: ["Espírito Santo", "Minas Gerais", "Rio de Janeiro", "São Paulo"],
  "Centro-Oeste": ["Distrito Federal", "Goiás", "Mato Grosso", "Mato Grosso do Sul"]
};

// Lista de todos os estados do Brasil
const allBrazilianStates = [
  "Acre", "Alagoas", "Amapá", "Amazonas", "Bahia", "Ceará", "Distrito Federal", "Espírito Santo", 
  "Goiás", "Maranhão", "Mato Grosso", "Mato Grosso do Sul", "Minas Gerais", "Pará", "Paraíba", 
  "Paraná", "Pernambuco", "Piauí", "Rio de Janeiro", "Rio Grande do Norte", "Rio Grande do Sul", 
  "Rondônia", "Roraima", "Santa Catarina", "São Paulo", "Sergipe", "Tocantins"
];

// Função para obter a cor da região
const getRegionColor = (regionType: string): string => {
  const colorMap: Record<string, string> = {
    "Norte": "bg-emerald-500",
    "Nordeste": "bg-indigo-500",
    "Sul": "bg-amber-500",
    "Sudeste": "bg-rose-500",
    "Centro-Oeste": "bg-purple-500"
  };
  
  return colorMap[regionType] || "bg-gray-400";
};

// Função para obter o valor de região com base no tipo
const getRegionValue = (project: any, regionType: string): string => {
  // Para compatibilidade com dados existentes, mapeamos regiões geográficas
  // considerando o estado do projeto
  const estadoToRegiao: Record<string, string> = {
    // Norte
    "AC": "Norte", "AM": "Norte", "AP": "Norte", "PA": "Norte", "RO": "Norte", "RR": "Norte", "TO": "Norte",
    // Nordeste
    "AL": "Nordeste", "BA": "Nordeste", "CE": "Nordeste", "MA": "Nordeste", "PB": "Nordeste", 
    "PE": "Nordeste", "PI": "Nordeste", "RN": "Nordeste", "SE": "Nordeste",
    // Sul
    "PR": "Sul", "RS": "Sul", "SC": "Sul",
    // Sudeste
    "ES": "Sudeste", "MG": "Sudeste", "RJ": "Sudeste", "SP": "Sudeste",
    // Centro-Oeste
    "DF": "Centro-Oeste", "GO": "Centro-Oeste", "MS": "Centro-Oeste", "MT": "Centro-Oeste"
  };
  
  // Obter região com base no estado do projeto
  const estado = project.estado || "RJ";
  return estadoToRegiao[estado] || "Sudeste"; // Padrão para RJ
};

// Tipos de região
type RegionType = "Norte" | "Nordeste" | "Sul" | "Sudeste" | "Centro-Oeste";

// Adicionando a nova prop
interface ProjectRegionProps {
  onBackToStatus: () => void;
}

const ProjectRegion: React.FC<ProjectRegionProps> = ({ onBackToStatus }) => {
  const { toast } = useToast();
  
  // Estados para controlar os filtros de região selecionados
  const [selectedRegionType, setSelectedRegionType] = useState<RegionType>("Sudeste");
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  
  // Estado para armazenar os projetos filtrados
  const [filteredProjects, setFilteredProjects] = useState(projectsData);
  
  // Valores únicos disponíveis para o tipo de região selecionado
  const [availableRegionValues, setAvailableRegionValues] = useState<string[]>([]);
  
  // Atualizar valores disponíveis quando o tipo de região muda
  useEffect(() => {
    console.log("[ProjectRegion] useEffect - Calculando availableRegionValues para:", selectedRegionType);
    // Obter os estados associados à região selecionada
    const estados = estadosPorRegiao[selectedRegionType] || [];
    setAvailableRegionValues(estados);
    setSelectedRegions([]);
  }, [selectedRegionType]);
  
  // Atualizar projetos filtrados quando os filtros forem alterados
  useEffect(() => {
    console.log("[ProjectRegion] useEffect - Filtrando projetos. Regiões selecionadas:", selectedRegions, "Tipo:", selectedRegionType);
    if (selectedRegions.length === 0) {
      // Filtrar projetos por região geográfica selecionada
      const filtered = projectsData.filter(project => {
        const projectRegion = getRegionValue(project, selectedRegionType);
        return projectRegion === selectedRegionType;
      });
      setFilteredProjects(filtered.length > 0 ? filtered : projectsData);
      return;
    }

    // Se houver estados específicos selecionados dentro da região
    const filtered = projectsData.filter(project => {
      return selectedRegions.includes(project.estado);
    });
    console.log("[ProjectRegion] Projetos filtrados:", filtered);
    setFilteredProjects(filtered);
  }, [selectedRegionType, selectedRegions]);
  
  // Função para alternar a seleção de um tipo de região
  const handleRegionTypeChange = (regionType: string) => {
    console.log("[ProjectRegion] handleRegionTypeChange para:", regionType);
    setSelectedRegionType(regionType as RegionType);
  };
  
  // Função para selecionar um estado específico dentro da região
  const handleEstadoRegionalChange = (estado: string) => {
    console.log("[ProjectRegion] handleEstadoRegionalChange:", estado);
    setSelectedRegions(estado ? [estado] : []);
  };
  
  console.log("[ProjectRegion] Renderizando. Tipo Selecionado:", selectedRegionType, "Regiões:", selectedRegions, "Projetos Filtrados:", filteredProjects.length);

  return (
    <>
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={onBackToStatus} aria-label="Voltar para Status do Projeto">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <MapPin className="h-6 w-6 text-[#0e7cb4]" />
            <h1 className="text-2xl font-bold text-gray-800">Projetos por Região</h1>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <h2 className="text-lg font-medium mb-4">Selecione o Tipo de Região</h2>
          <div className="flex flex-wrap gap-3">
            {regionTypes.map((type) => (
              <Button
                key={type}
                variant={selectedRegionType === type ? "default" : "outline"}
                className={`flex items-center gap-2 ${
                  selectedRegionType === type ? "bg-[#0e7cb4] text-white" : ""
                }`}
                onClick={() => handleRegionTypeChange(type)}
              >
                <span className={`h-3 w-3 rounded-full ${getRegionColor(type)}`}></span>
                {type}
              </Button>
            ))}
          </div>
        </div>
        
        <div key={selectedRegionType} className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <h2 className="text-lg font-medium mb-4">
            Filtrar por {selectedRegionType}
          </h2>
          
          <div className="max-w-md">
            <p className="text-sm text-gray-600 mb-3">
              <span className="font-medium">Estados da Região {selectedRegionType}</span>
              <br />
              {availableRegionValues.length > 0 && (
                <>
                  Selecione um estado para filtrar os projetos:
                  <Select onValueChange={handleEstadoRegionalChange} value={selectedRegions[0] || ""}>
                    <SelectTrigger className="w-full mt-2">
                      <SelectValue placeholder={`Todos os estados da região ${selectedRegionType}`} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px] overflow-y-auto">
                      {availableRegionValues.map((estado) => (
                        <SelectItem key={estado} value={estado}>
                          {estado}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </p>
            
            {selectedRegions.length > 0 ? (
              <div className="mt-4 p-3 bg-slate-50 rounded-md">
                <p className="text-sm">
                  Filtrando projetos no estado: <Badge variant="outline" className="ml-1">{selectedRegions[0]}</Badge>
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setSelectedRegions([])} 
                  className="mt-2"
                >
                  Mostrar todos os estados da região
                </Button>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-slate-50 rounded-md">
                <p className="text-sm">
                  Filtrando projetos na região {selectedRegionType}
                </p>
              </div>
            )}
          </div>
        </div>
        
        {filteredProjects.length === 0 ? (
           <div className="text-center py-10 bg-white rounded-lg shadow-sm">
            <p className="text-gray-500">Nenhum projeto encontrado com os filtros selecionados.</p>
            <p className="text-xs text-gray-400 mt-1">(Não há projetos registrados na região {selectedRegionType})</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="col-span-full mb-4">
              {selectedRegions.length > 0 ? (
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="mb-2">Projetos filtrados por estado:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRegions.map((region) => (
                      <Badge key={region} variant="outline">{region}</Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mb-4">Mostrando projetos da região {selectedRegionType} ({filteredProjects.length}). Selecione um estado específico para refinar os resultados.</p>
              )}
            </div>
            {filteredProjects.map((project) => (
              <Card key={project.id} className="overflow-hidden">
                 <div className="h-[150px] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h2 className="font-medium text-lg">{project.name}</h2>
                  </div>
                  <div className="mb-3 space-y-1">
                    <p className="text-sm text-gray-600">ID Projeto: {project.id}</p>
                    <p className="text-sm text-gray-600">Bairro: {project.bairro || 'N/A'}</p>
                    <p className="text-sm text-gray-600">Cidade: {project.cidade || 'N/A'}</p>
                    <p className="text-sm text-gray-600">Estado: {project.estado || 'N/A'}</p>
                    <p className="text-sm text-gray-600">Região: {getRegionValue(project, selectedRegionType)}</p>
                  </div>
                  <div className="flex items-center mb-3">
                    <span className={`h-2 w-2 rounded-full ${
                      project.status === "Em implementação" ? "bg-green-500" : "bg-gray-500"
                    } mr-2`}></span>
                    <p className="text-sm font-medium">{project.status}</p>
                  </div>
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Progresso do Projeto</p>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-[#0e7cb4] h-2.5 rounded-full"
                        style={{ width: `${project.progress}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500">Tokens Disponíveis</p>
                      <p className="text-[#0e7cb4] font-bold">{project.tokens}</p>
                    </div>
                    <Link to={`/project/${project.id}`}>
                      <Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => {
                        toast({
                          title: "Acessando projeto",
                          description: `Navegando para os detalhes de ${project.name}`,
                        });
                      }}>
                        <LogIn className="h-4 w-4" />
                        Entrar
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectRegion; 